'use client'

import * as React from 'react'
import { CloseIcon } from '@/src/icons'
import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'

import { cn } from '@/registry/reapit/lib/utils'
import { asChildProps } from '@/registry/reapit/lib/as-child-props'

// ---------------------------------------------------------------------------
// Sheet — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Sheet wraps Dialog (already on Base UI from Phase 1).
// Changes:
//   Dialog.Overlay → Dialog.Backdrop
//   Dialog.Content → Dialog.Popup
//   data-[state=open/closed] → data-[open] / data-[starting-style] / data-[ending-style]
//   asChild → render (via asChildProps shim)
// ---------------------------------------------------------------------------

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  asChild,
  children,
  ...props
}: Omit<React.ComponentProps<typeof SheetPrimitive.Trigger>, 'render'> & {
  asChild?: boolean
}) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...asChildProps(asChild, children)} {...props} />
}

function SheetClose({
  asChild,
  children,
  ...props
}: Omit<React.ComponentProps<typeof SheetPrimitive.Close>, 'render'> & {
  asChild?: boolean
}) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...asChildProps(asChild, children)} {...props} />
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

// ---------------------------------------------------------------------------
// SheetOverlay (maps to Base UI Backdrop)
// ---------------------------------------------------------------------------

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Backdrop>) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        // Base UI: transition via data-[starting-style] / data-[ending-style]
        'fixed inset-0 z-50 bg-black/50 transition-opacity duration-150 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// SheetContent (maps to Base UI Portal > Backdrop + Popup)
// ---------------------------------------------------------------------------

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  width = 'default',
  ...props
}: Omit<React.ComponentProps<typeof SheetPrimitive.Popup>, 'render'> & {
  side?: 'top' | 'right' | 'bottom' | 'left'
  showCloseButton?: boolean
  /** DS drawer widths: mobile=375px, desktop=480px */
  width?: 'default' | 'mobile' | 'desktop'
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-width={width}
        className={cn(
          // Base UI: transition via data-[starting-style] / data-[ending-style] instead of data-[state=open/closed]
          'fixed z-50 flex flex-col gap-4 bg-background shadow-lg transition-transform ease-in-out duration-300',
          side === 'right' &&
            'inset-y-0 right-0 h-full border-l translate-x-0 data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full',
          side === 'right' && width === 'default' && 'w-3/4 sm:max-w-sm',
          side === 'right' && width === 'mobile' && 'w-full max-w-[375px]',
          side === 'right' && width === 'desktop' && 'w-full max-w-[480px]',
          side === 'left' &&
            'inset-y-0 left-0 h-full border-r translate-x-0 data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full',
          side === 'left' && width === 'default' && 'w-3/4 sm:max-w-sm',
          side === 'left' && width === 'mobile' && 'w-full max-w-[375px]',
          side === 'left' && width === 'desktop' && 'w-full max-w-[480px]',
          side === 'top' &&
            'inset-x-0 top-0 h-auto border-b translate-y-0 data-[starting-style]:-translate-y-full data-[ending-style]:-translate-y-full',
          side === 'bottom' &&
            'inset-x-0 bottom-0 h-auto border-t translate-y-0 data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          // DS: close icon is icon-md (size-5 = 20 px).
          <SheetPrimitive.Close className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
            <CloseIcon />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  )
}

function SheetHeader({
  className,
  overline,
  supplementary,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  /** DS: small overline text above the title */
  overline?: React.ReactNode
  /** DS: supplementary info below the title */
  supplementary?: React.ReactNode
}) {
  return (
    <div data-slot="sheet-header" className={cn('flex flex-col gap-1.5 p-4', className)} {...props}>
      {overline && (
        <p data-slot="sheet-overline" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {overline}
        </p>
      )}
      {children}
      {supplementary && (
        <p data-slot="sheet-supplementary" className="text-sm text-muted-foreground">
          {supplementary}
        </p>
      )}
    </div>
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-footer" className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    // DS: Drawer title uses heading L = 20 px / semi-bold (text-lg = 20px = DS L, font-semibold).
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-lg leading-7 font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

// --- lookup maps ---

const sheetBodyPaddingClasses = {
  sm: 'px-3 pb-3',
  md: 'px-4 pb-4',
  lg: 'px-6 pb-6',
} as const

// --- SheetBody ---

export namespace SheetBody {
  export type Padding = keyof typeof sheetBodyPaddingClasses

  export interface Props extends React.ComponentProps<'div'> {
    /**
     * Horizontal + bottom padding inside the body.
     * Default: 'md' -> px-4 pb-4
     */
    padding?: Padding
  }
}

/**
 * Scrollable content area between `SheetHeader` and `SheetFooter`.
 * Renders as a flex column that fills remaining height and scrolls vertically.
 *
 * ```tsx
 * <SheetContent>
 *   <SheetHeader><SheetTitle>Edit</SheetTitle></SheetHeader>
 *   <SheetBody>
 *     <Field>...</Field>
 *   </SheetBody>
 *   <SheetFooter>...</SheetFooter>
 * </SheetContent>
 * ```
 */
export function SheetBody({ padding = 'md', className, ...props }: SheetBody.Props) {
  return (
    <div
      data-slot="sheet-body"
      className={cn('flex flex-1 flex-col gap-6 overflow-y-auto', sheetBodyPaddingClasses[padding], className)}
      {...props}
    />
  )
}

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription }
